import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Trash2, Phone, Calendar, Mail, MessageSquare, Building2, Users, Briefcase, FolderOpen, Settings, Star, Calculator } from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminContactPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    // Check if admin is logged in
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/contact/${id}/status`, { status });
      fetchSubmissions();
      if (selectedSubmission?.id === id) {
        setSelectedSubmission({ ...selectedSubmission, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${id}`);
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const filteredSubmissions = submissions.filter(sub => 
    statusFilter === 'all' || sub.status === statusFilter
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-slate-100 text-slate-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminSidebar pageTitle="Contact Submissions" pageIcon={MessageSquare}>
      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Submissions</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-slate-600 text-sm">
          {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No Contact Submissions</h3>
          <p className="text-slate-600">Contact form submissions will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Service</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">
                      {submission.name || `${submission.firstName || ''} ${submission.lastName || ''}`.trim() || 'Unknown'}
                    </div>
                    <div className="text-sm text-slate-500">{submission.email}</div>
                    {submission.form_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
                        submission.form_type === 'consultation' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'
                      }`}>
                        {submission.form_type === 'consultation' ? 'Project Inquiry' : 'General'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {submission.service || submission.subject || 'Not specified'}
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-sm">
                    {formatDate(submission.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <Select
                      value={submission.status}
                      onValueChange={(value) => updateStatus(submission.id, value)}
                    >
                      <SelectTrigger className={`w-32 h-8 text-xs ${getStatusColor(submission.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedSubmission(submission)}
                        className="text-slate-600 hover:text-cyan-600"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSubmission(submission.id)}
                        className="text-slate-600 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Contact Submission</h2>
                  {selectedSubmission.form_type && (
                    <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
                      selectedSubmission.form_type === 'consultation' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'
                    }`}>
                      {selectedSubmission.form_type === 'consultation' ? 'Project Inquiry' : 'General Contact'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-slate-500">Name</label>
                  <p className="text-slate-900 font-medium">
                    {selectedSubmission.name || `${selectedSubmission.firstName || ''} ${selectedSubmission.lastName || ''}`.trim() || 'Unknown'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Status</label>
                  <p>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedSubmission.status)}`}>
                      {selectedSubmission.status.charAt(0).toUpperCase() + selectedSubmission.status.slice(1)}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-900">{selectedSubmission.email}</span>
                </div>
                {selectedSubmission.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedSubmission.phone}`} className="text-cyan-600 hover:underline">
                      {selectedSubmission.phone}
                    </a>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">
                  {selectedSubmission.subject ? 'Subject' : 'Service Interested In'}
                </label>
                <p className="text-slate-900">{selectedSubmission.service || selectedSubmission.subject || 'Not specified'}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Message</label>
                <p className="text-slate-900 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg mt-1">
                  {selectedSubmission.message}
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                <span>Submitted {formatDate(selectedSubmission.created_at)}</span>
              </div>

              <div className="pt-4 border-t flex justify-end gap-3">
                {selectedSubmission.phone && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(`tel:${selectedSubmission.phone}`)}
                  >
                    <Phone className="w-4 h-4 mr-2" /> Call
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteSubmission(selectedSubmission.id);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminSidebar>
  );
};

export default AdminContactPage;
